import queryString from 'query-string'
import { chunk, cloneDeep, defaultsDeep, isEmpty, isNil, startCase, toUpper } from 'lodash'
import moment from 'moment'
import { toast } from '@/components/toasts/toast.helpers'
import {
  formatCallDuration,
  formatContent,
  formatAiCall,
  getAgentOptions,
  getDateRangeFromSavedFilter,
  getTagsWithCategories,
  hasChannelAccess,
} from '@/utils/helpers'

import { apiService, fetchingAPI } from '@/api'
import {
  CALL_SUMMARY_PARENT,
  EXTRACTED_DATA_PARENT,
  enrichCallsWithTagCategories,
  flattenSummaryOntoCall,
} from '@/reducers/customColumns/customColumns.helpers'
import { history } from '@/entry'
import { initialPlaybookBody } from '@/views/Playbooks/playbook.helpers'
import { AGENT_EVENT_SECTION, TOOL_CALL_SECTION } from '@/views/Calls/components/helpers'
import { parseToCsv } from '@/components/helpers/parseToCsv'
import { exportCsv } from '@/components/helpers/exportToCsv'
import { CALL_EXPLORER_FILTER_TYPE } from '@/reducers/savedFilters/savedFilters.constants'
import { fetchSavedFilters } from '@/reducers/savedFilters/savedFilters.actions'
import { fetchMediaForCallId } from '@/reducers/media/media.actions'
import { selectSupportWindowOpenForOrg } from '@/reducers/supportAccess/supportAccess.redux'

import { CALL_SEARCH_RESULTS_LIMIT, CHANNEL_TYPE, CSV_DOWNLOAD_WARNING } from '@/utils/constants'
import {
  clearFiltersAndSelectOrg,
  initialState,
  setCallExplorerCall,
  setCallExplorerData,
  setCallExplorerLoading,
  setCalls,
  setCsvAbortController,
  setData,
  setError,
  setFilters,
  setLoading,
  setTotalCount,
} from './callSearch.redux'
import { formatCallExplorerQueryString, mapParamIds, parseParams } from './helpers'

let countAbortController = null

export const formatSection = (section, filters) => {
  const { includes, selected } = filters

  const formattedEntries = selected.map((context) => {
    const formattedEntry = {
      category: '',
      entry: '',
      decklist_entry: '',
      cid: '',
    }

    const splitContext = context.split(' - ')
    const isNested = splitContext.length > 3
    if (isNested) {
      splitContext.forEach((contextSection, index) => {
        // index 0 is section which we already know
        if (index === 0) {
          formattedEntry.cid = contextSection
        } else if (index === 2) {
          formattedEntry.category = contextSection
        } else if (index === 3) {
          formattedEntry.entry = contextSection
        } else if (index === 4) {
          formattedEntry.decklist_entry =
            contextSection === 'No response used' ? '' : contextSection
        }
      })
    } else {
      /* eslint-disable-next-line prefer-destructuring */
      formattedEntry.entry = splitContext[2]
      /* eslint-disable-next-line prefer-destructuring */
      formattedEntry.cid = splitContext[0]
    }

    return formattedEntry
  })

  return {
    section,
    includes,
    filters: formattedEntries,
  }
}

const findNoResUsed = (contextStr) => contextStr.toLowerCase().includes('no response used')
export const formatNoResponseUsed = (deckFilters, playbookData) => {
  const formattedDecklists = []

  playbookData.forEach((playbook) => {
    const { deck, cid } = playbook

    // Find context strings that match the current playbook's CID
    const matchingContexts = deckFilters.selected.filter((contextStr) => {
      const [contextCid] = contextStr.split(' - ')
      return contextCid === cid
    })

    if (isEmpty(matchingContexts)) {
      return // No matching context for this playbook, move to next
    }

    matchingContexts.forEach((contextStr) => {
      const [cid, , category, entry] = contextStr.split(' - ')
      const selectedCategory = deck.find((catObj) => catObj.category === category)
      if (!selectedCategory) return

      const selectedEntry = selectedCategory.items.find((entryObj) => entryObj.item === entry)
      if (!selectedEntry) return

      selectedEntry.items
        .filter((decklistEntry) => !decklistEntry.toLowerCase().includes('no response used'))
        .forEach((decklistEntry) => {
          formattedDecklists.push({
            category,
            entry,
            decklist_entry: decklistEntry,
            cid,
          })
        })
    })
  })

  return {
    section: 'deck',
    includes: !deckFilters.includes,
    is_no_response_used: true,
    filters: formattedDecklists,
  }
}

export const formatEvents = (filters, playbookData) => {
  const { checklist, deck, notifications, postcall } = filters
  const formattedSections = []

  if (!isEmpty(checklist.selected)) {
    formattedSections.push(formatSection('checklist', checklist))
  }
  if (!isEmpty(deck.selected)) {
    const requiredParents = new Set()
    // this could be optimized by putting the filters in this for each too
    // but that's too much work for such a small array of stuff
    // if you have any decklist items selected regardless if you select "occurs"
    // we need to only pull calls where the parent events happens
    deck.selected.forEach((contextStr) => {
      const splitContext = contextStr.split(' - ')
      // if context is less than 4 then it's not a decklist item
      // e.g. deck - Objections - Budget - "Have you tried making more monies?"
      if (splitContext.length < 5) {
        return
      }
      const splitParent = splitContext.slice(0, 4)
      const parentContext = splitParent.join(' - ')
      requiredParents.add(parentContext)
    })
    const noResUsedSelected = deck.selected.filter(findNoResUsed)
    const selectedDecklists = deck.selected.filter((contextStr) => {
      const isNoResUsed = contextStr.toLowerCase().includes('no response used')
      const isRequiredParent = requiredParents.has(contextStr)
      return !(isRequiredParent || isNoResUsed)
    })

    if (!isEmpty(selectedDecklists)) {
      formattedSections.push(formatSection('deck', { ...deck, selected: selectedDecklists }))
    }
    if (!isEmpty(requiredParents)) {
      formattedSections.push(
        formatSection('deck', { ...deck, includes: true, selected: Array.from(requiredParents) })
      )
    }
    if (!isEmpty(noResUsedSelected)) {
      formattedSections.push(
        formatNoResponseUsed({ ...deck, selected: noResUsedSelected }, playbookData)
      )
    }
  }
  if (!isEmpty(notifications.selected)) {
    formattedSections.push(formatSection('notifications', notifications))
  }
  if (!isEmpty(postcall.selected)) {
    formattedSections.push(formatSection('classified_postcall', postcall))
  }
  return formattedSections
}

export const formatCallFilters = (filters, playbookData) => {
  // Ignore everything else for call_id search
  if (filters.call_ids) {
    const body = { call_ids: filters.call_ids, organization_id: filters.organizationId }

    if (!isEmpty(filters.channel)) {
      body.call_types = [filters.channel]
    }

    if (!isEmpty(filters.scorecards)) {
      body.scorecards = filters.scorecards.map((scorecard) => scorecard.sid)
    }

    return body
  }

  const body = {
    organization_id: filters.organizationId,
    start_date: filters.startDate,
    end_date: filters.endDate,
    agent_ids: filters.agents?.map((item) => item.value),
    tags: filters.tags?.map((item) => ({ category_id: item.tag_cat, tag_id: item.value })),
    playbook_cids: filters.playbooks?.map((item) => item.value),
    ending_sentiment: filters.endingSentiment?.map((item) => item.value),
    attachments: filters.attachments?.map((item) => item.value),
    direction: filters.direction || undefined,
    call_duration: formatCallDuration(filters.callDuration),
    max_call_duration: isEmpty(filters.maxCallDuration) ? 0 : filters.maxCallDuration,
    voip_campaign_names: filters.voipCampaigns?.map((item) => item.value),
    outbound_campaign_ids: filters.outboundCampaigns?.map((item) => item.value),
    ai_agent_outcomes: filters.aiAgentOutcomes?.map((item) => item.value),
    ai_agent_disconnection_reasons: filters.aiAgentDisconnectionReasons?.map((item) => item.value),
    keywords: filters.keywords?.map((item) => item.value),
    keyword_search_operator: toUpper(filters.keywordOptions?.logic),
    keyword_side_filter:
      filters.keywordOptions?.side === 'both' ? null : toUpper(filters.keywordOptions?.side),
    is_ai_call: formatAiCall(filters),
  }
  if (filters.isWin !== '') {
    body.is_win = filters.isWin
  }

  if (!isEmpty(filters.voipCustomerIds)) {
    body.voip_customer_ids = filters.voipCustomerIds
      .map((id) => (typeof id === 'string' ? id : id?.value))
      .filter(Boolean)
  }

  const events = formatEvents(filters, playbookData)

  if (!isEmpty(events)) {
    body.events = events
  }
  if (filters.scorecards) {
    body.scorecards = filters.scorecards.map((scorecard) => scorecard.sid)
  }
  if (filters.scoredStatus && filters.scoredStatus !== 'any') {
    body.scored_status = filters.scoredStatus
  }
  if (filters.scorecardThreshold) {
    body.scorecard_threshold = filters.scorecardThreshold
  }
  if (!isEmpty(filters.channel)) {
    body.call_types = [filters.channel]
  }
  if (filters.minMessageCount) {
    body.min_message_count = filters.minMessageCount
  }
  if (filters.maxMessageCount) {
    body.max_message_count = filters.maxMessageCount
  }
  body.limit = CALL_SEARCH_RESULTS_LIMIT

  // pick_list field filters: one entry per field with a selection. Each call
  // must match all entries (AND); within an entry, any selected option matches.
  // A single option can span several taxonomies (same name across playbooks), so
  // expand its categoryUuids — the reporting API resolves each to its taxonomy.
  const bitFieldFilters = Object.entries(filters.bitFields ?? {})
    .map(([fieldId, selected]) => ({
      field_id: Number(fieldId),
      taxonomy_category_uuids: selected.flatMap((option) => option.categoryUuids),
    }))
    .filter((entry) => !isEmpty(entry.taxonomy_category_uuids))
  if (!isEmpty(bitFieldFilters)) {
    body.bit_fields = bitFieldFilters
  }

  // Boolean bit fields filter on a single true/false value via a separate param.
  const booleanFieldFilters = Object.entries(filters.booleanFields ?? {})
    .filter(([, value]) => !isNil(value))
    .map(([fieldId, value]) => ({ field_id: Number(fieldId), value }))
  if (!isEmpty(booleanFieldFilters)) {
    body.boolean_fields = booleanFieldFilters
  }

  if (!isEmpty(filters.dispositions?.selected) || filters.dispositions?.includes !== undefined) {
    const mappedDispositions = filters.dispositions.selected?.map((d) => d.value) || []

    if (!isEmpty(mappedDispositions)) {
      body.dispositions = {
        filters: mappedDispositions,
        includes: filters.dispositions.includes ?? true,
      }
    }
  }
  return body
}

export const fetchCallCount = ({ filters, playbookData }) => {
  return async (dispatch) => {
    if (countAbortController) {
      countAbortController.abort()
    }
    countAbortController = new AbortController()
    const { signal } = countAbortController

    const { limit, ...body } = formatCallFilters(filters, playbookData)
    body.allow_unlimited_date_range = true

    try {
      const { total_count } = await fetchingAPI(
        `${apiService.reporting}/api/calls/count`,
        'POST',
        dispatch,
        JSON.stringify(body),
        undefined,
        undefined,
        undefined,
        signal
      )

      dispatch(setTotalCount(total_count))
    } catch (error) {
      if (error?.name === 'AbortError') {
        return
      }
      dispatch(setTotalCount(null))
    }
  }
}

export const fetchCalls = ({ filters, playbookData }) => {
  return async (dispatch) => {
    dispatch(setLoading({ calls: true }))

    if (!filters.call_ids) {
      const currentFiltersQueryStr = formatCallExplorerQueryString(filters)
      history.push(`/explorer?${currentFiltersQueryStr}`)
      dispatch(setTotalCount(null))
      dispatch(fetchCallCount({ filters, playbookData }))
    }
    const body = formatCallFilters(filters, playbookData)
    body.allow_unlimited_date_range = true

    try {
      const { calls } = await fetchingAPI(
        `${apiService.reporting}/api/calls`,
        'POST',
        dispatch,
        JSON.stringify(body)
      )

      const callsWithExtractedData = calls.map((call) => {
        if (!isEmpty(call.extracted_data)) {
          return flattenSummaryOntoCall(call, call.extracted_data, EXTRACTED_DATA_PARENT)
        }
        return call
      })

      dispatch(setCalls(callsWithExtractedData))
    } catch {
      dispatch(setCalls([]))
      toast.error('Failed to fetch calls')
    } finally {
      dispatch(setLoading({ calls: false }))
    }
  }
}

// Call Search Helpers
export const fetchCallsFromLatestFilters = () => async (dispatch, getState) => {
  const { filters, data, csvAbortController } = getState().callSearch
  const { active_channels: activeChannels = [] } = getState().currentOrganization
  const { organizationid: currentUserOrg } = getState().currentUser
  const isBaltoAdmin = currentUserOrg === 1

  if (csvAbortController && !csvAbortController.signal.aborted) {
    // eslint-disable-next-line no-alert
    if (!window.confirm(CSV_DOWNLOAD_WARNING)) {
      return
    }
    csvAbortController.abort()
  }
  const channel = filters.channel || CHANNEL_TYPE.CALL

  // Don't fetch if org doesn't have access to the selected channel
  if (!hasChannelAccess(activeChannels, channel, isBaltoAdmin)) {
    dispatch(setCalls([]))
    dispatch(setTotalCount(null))
    return
  }

  if (filters.organizationId) {
    dispatch(fetchCalls({ filters, playbookData: data.playbookData }))
  }
}
export const fetchScorecardsByOrg =
  (organizationId, scorecardType = null) =>
  async (dispatch) => {
    dispatch(setLoading({ scorecards: true }))
    let url = `${apiService.scorecard}/scoring/scorecards/current?requested_organization_id=${organizationId}`
    if (scorecardType) {
      url += `&scorecard_type=${scorecardType}`
    }
    try {
      const apiScorecardConfigs = await fetchingAPI(url, 'GET', dispatch)
      const sortedScorecardConfigs = apiScorecardConfigs.sort((a, b) =>
        a.name?.localeCompare(b.name)
      )

      dispatch(setData({ scorecardConfigs: sortedScorecardConfigs }))
    } catch (err) {
      toast.error(`Failed to fetch scorecards for organization: ${organizationId}`, { cause: err })
    } finally {
      dispatch(setLoading({ scorecards: false }))
    }
  }

export const applyFilterAndFetchCalls = (accessor, value) => async (dispatch) => {
  dispatch(setFilters({ [accessor]: value }))

  await dispatch(fetchCallsFromLatestFilters())
}

export const applyFiltersAndFetchCalls = (filters) => async (dispatch) => {
  const formattedFilters = dispatch(mapParamIds(filters))
  dispatch(setFilters(formattedFilters))

  await dispatch(fetchCallsFromLatestFilters())
}

export const removeFilters = (accessor) => async (dispatch) => {
  dispatch(setFilters({ [accessor]: initialState.filters[accessor] }))
}

export const removeFilterAndFetchCalls = (accessor) => async (dispatch) => {
  dispatch(removeFilters(accessor))

  await dispatch(fetchCallsFromLatestFilters())
}

// Clears all filters except the organization selected
export const clearAllFilters = (selectedOrganizationId) => async (dispatch, getState) => {
  const { organizationid: defaultOrganizationId } = getState().currentUser
  const organizationId = selectedOrganizationId || defaultOrganizationId

  // Clear saved filter
  dispatch(setData({ selectedSavedFilterId: null }))

  // If no org is passed, use the user's default org.
  dispatch(clearFiltersAndSelectOrg(organizationId))
}

export const clearAllFiltersAndFetchCalls = (selectedOrganizationId) => async (dispatch) => {
  dispatch(clearAllFilters(selectedOrganizationId))

  await dispatch(fetchCallsFromLatestFilters())
}

export const clearSavedFilter = (shouldLoadCalls) => (dispatch) => {
  if (shouldLoadCalls) {
    dispatch(clearAllFiltersAndFetchCalls())
  } else {
    dispatch(clearAllFilters())
  }
}

export const setSavedFilterById =
  (filterId, shouldLoadCalls = true) =>
  async (dispatch, getState) => {
    const { savedFilterList } = getState().savedFilters
    const savedCallExplorerFilters = savedFilterList[CALL_EXPLORER_FILTER_TYPE]

    const filterToApply = savedCallExplorerFilters?.find((filter) => filter.uuid === filterId)

    if (filterToApply?.filters) {
      dispatch(setData({ selectedSavedFilterId: filterId }))

      // Set actual start/end times based on static range
      const { startDate, endDate } = getDateRangeFromSavedFilter(filterToApply.filters.dateRange)

      const updatedFilters = {
        ...filterToApply.filters,
        organizationId: filterToApply.filters.organization,
        scorecards: filterToApply.filters.scorecards || [],
        startDate,
        endDate,
        // Set even when absent: setFilters merges, so a filter saved without one would keep the previous pick.
        dateRange: filterToApply.filters.dateRange,
      }

      dispatch(setFilters(updatedFilters))

      if (shouldLoadCalls) {
        const updatedMergedFilters = getState().callSearch.filters
        const { active_channels: activeChannels = [] } = getState().currentOrganization
        const { organizationid: currentUserOrg } = getState().currentUser
        const isBaltoAdmin = currentUserOrg === 1
        const channel = updatedMergedFilters.channel || CHANNEL_TYPE.CALL

        // Don't fetch if org doesn't have access to the selected channel
        if (!hasChannelAccess(activeChannels, channel, isBaltoAdmin)) {
          dispatch(setCalls([]))
          return
        }

        // TODO: These filters are not sanitized from the BE, and the BE is just a JSONB string.
        // We should sanitize these filters before sending them to the API
        await dispatch(fetchCalls({ filters: updatedMergedFilters }))
      }
    }
  }

export const fetchSummariesForCallIds = (call_ids) => async (dispatch, getState) => {
  const validCallIds = call_ids.filter((id) => id != null)

  if (validCallIds.length === 0) {
    return // No valid call IDs to fetch
  }

  const CHUNK_SIZE = 1000 // Process call_ids in chunks of 1000
  const MAX_WORKERS = 10 // Maximum number of concurrent workers
  const fetchedCallIds = new Set()

  try {
    // Split call_ids into chunks of CHUNK_SIZE
    const callIdChunks = chunk(validCallIds, CHUNK_SIZE)

    // Worker pool to process chunks with a maximum of MAX_WORKERS
    const results = []
    const queue = [...callIdChunks]
    const workers = Array.from({ length: MAX_WORKERS }, async () => {
      while (queue.length > 0) {
        const chunk = queue.shift()
        try {
          const result = await fetchingAPI(
            `${apiService.summary}/summaries/multiple`,
            'POST',
            dispatch,
            JSON.stringify({ call_ids: chunk })
          )
          results.push(result)
          chunk.forEach((id) => fetchedCallIds.add(id))
        } catch (err) {
          // Failed-chunk ids are deliberately NOT added to fetchedCallIds: their
          // call_summary stays undefined so the next search/render retries them,
          // instead of being marked "requested, empty" and silently excluded.
          console.error('Error fetching chunk:', chunk, err)
        }
      }
    })

    // Wait for all workers to complete
    await Promise.all(workers)

    // If every chunk failed (e.g. a 403 from an unredacted org), dispatching setCalls
    // with a fresh array would re-trigger the summary-loading effect and loop forever.
    if (isEmpty(fetchedCallIds)) {
      return
    }

    // Re-read calls at merge time rather than snapshotting at entry. A bulk load can run
    // for several seconds, so a filter/date-range change mid-load must not be clobbered by
    // a stale snapshot. Summaries for call_ids no longer present are dropped by the lookup.
    const { calls } = getState().callSearch
    const callsCopy = cloneDeep(calls).map((call) =>
      fetchedCallIds.has(call.call_id)
        ? {
            ...call,
            // Empty object marks the call_ids we fetched successfully: an empty
            // call_summary means we asked and got nothing back, so we won't
            // re-request it later.
            call_summary: {},
          }
        : call
    )

    // Merge summaries from all chunks. Indexed lookup instead of findIndex: the bulk
    // search path merges up to 10k summaries into 10k calls, where a linear scan per
    // summary costs ~300ms of blocked main thread. First index wins on duplicate
    // call_ids, matching the findIndex behavior this replaced.
    const callIndexById = new Map()
    callsCopy.forEach((call, index) => {
      if (!callIndexById.has(call.call_id)) {
        callIndexById.set(call.call_id, index)
      }
    })
    results.flat().forEach((summary) => {
      const callIndex = callIndexById.get(summary.call_id)
      if (!isNil(callIndex)) {
        let parsedSummary = summary.parsed_summary

        if (!isEmpty(summary.parsed_editable_summary)) {
          parsedSummary = summary.parsed_editable_summary
          Object.keys(parsedSummary).forEach((key) => {
            if (parsedSummary[key] !== summary.parsed_summary[key]) {
              parsedSummary[key] = `${parsedSummary[key]} (Edited)`
            } else {
              parsedSummary[key] = formatContent(parsedSummary[key])
            }
          })
        }

        Object.assign(callsCopy[callIndex], { call_summary: parsedSummary })
        callsCopy[callIndex] = flattenSummaryOntoCall(
          callsCopy[callIndex],
          parsedSummary,
          CALL_SUMMARY_PARENT
        )

        if (!isEmpty(summary.extracted_data)) {
          Object.assign(callsCopy[callIndex], { extracted_data: summary.extracted_data })
          callsCopy[callIndex] = flattenSummaryOntoCall(
            callsCopy[callIndex],
            summary.extracted_data,
            EXTRACTED_DATA_PARENT
          )
        }
      }
    })

    dispatch(setCalls(callsCopy))
  } catch (err) {
    if (err?.status !== 404) {
      toast.error(err?.message || 'Failed to fetch summaries')
    }
  }
}

// Summaries are normally lazy-loaded only for the rows on screen, so searching a
// notetaker column would only match the current page. When the user searches one,
// pull summaries for every loaded result so the client-side filter covers the whole
// table. The loaded result set is already capped at CALL_SEARCH_RESULTS_LIMIT, so this
// is bounded to the same footprint as a client-side CSV export.
export const loadAllSummariesForSearch = () => async (dispatch, getState) => {
  const { loading, calls } = getState().callSearch
  // Guard against concurrent keystrokes kicking off duplicate bulk loads. setLoading
  // runs synchronously below, before the first await, so later keystrokes bail here.
  if (loading.summarySearch) {
    return
  }

  const callIdsWithoutSummaries = calls
    .filter(({ call_id, call_summary }) => !isNil(call_id) && isNil(call_summary))
    .map(({ call_id }) => call_id)

  if (isEmpty(callIdsWithoutSummaries)) {
    return
  }

  dispatch(setLoading({ summarySearch: true }))
  try {
    await dispatch(fetchSummariesForCallIds(callIdsWithoutSummaries))
  } finally {
    dispatch(setLoading({ summarySearch: false }))
  }
}

export const fetchVoipCustomerCalls = (call) => async (dispatch) => {
  const {
    voip_customer_id: voipCustomerId,
    organization_id: organizationId,
    call_id: callId,
  } = call.metadata

  try {
    const voipCustomerCallRes = await fetchingAPI(
      `${apiService.reporting}/api/calls/customer/${voipCustomerId}?organization_id=${organizationId}`
    )
    // we dont wanna show the call the user is looking at in the count
    const filteredCalls = voipCustomerCallRes
      .filter(({ call_id }) => call_id !== callId)
      .map(({ call_start_time, ...rest }) => {
        // +Z sets it to UTC
        const totalMs = new Date(`${call_start_time}Z`).getTime()
        return {
          call_start_time: totalMs,
          ...rest,
        }
      })
    dispatch(setData({ voipCustomerCalls: filteredCalls }))
  } catch (err) {
    if (err.message === 'No previous calls found with this customer') {
      return
    }
    toast.error('Failed to fetch calls with matching Voip Customer ID', { cause: err })
  }
}

export const fetchCall = (id, queryStr) => async (dispatch) => {
  let url = `${apiService.reporting}/api/calls/${id}`

  if (queryStr) {
    url += `?${queryStr}`
  }

  const call = await fetchingAPI(url, 'GET', dispatch)

  if (call.postcall_transcript.length) {
    call.copilotAnchors = call.postcall_transcript
      .flatMap((transcript) => transcript.data)
      .filter(
        (
          { type, criteria_name, scorecard_name } = {
            type: null,
            criteria_name: '',
            scorecard_name: '',
          }
        ) => type === 'anchor' && criteria_name && scorecard_name
      )
      .map((anchor, index) => {
        return {
          ...anchor,
          id: `anchor-${index}`,
        }
      })
  }

  // Set unique ids on events, since timestamps can be the same
  call.analysis = call.analysis.map((event, index) => {
    const { category, item, timestamp, section, sub_events: subEvents, question, answer } = event
    const eventId = `${index}`

    // set unique ids on subevents as well
    // since subevent timestamps can be the same
    let subEventsFormatted = subEvents
    if (subEvents) {
      // No Response Used for deck
      if (subEvents.length === 0) {
        subEventsFormatted = [
          {
            id: `${eventId}-0`,
            item: 'No Response Used',
            isNoResponse: true,
            timestamp,
            section,
          },
        ]
      } else {
        // Regular deck list events
        subEventsFormatted = subEvents.map((subevent, index) => {
          return { ...subevent, id: `${eventId}-${index}`, section }
        })
      }
    }

    return {
      id: eventId,
      category,
      item,
      timestamp,
      section,
      question,
      answer,
      ...(subEventsFormatted && { subEvents: subEventsFormatted }),
    }
  })

  // Flatten all events so deck list events appear in event timelines
  call.analysisFlattened = call.analysis.reduce((prev, currEvent) => {
    const { subEvents, ...eventProperties } = currEvent

    prev.push({
      ...eventProperties,
    })

    if (subEvents) {
      prev.push(...subEvents)
    }

    return prev
  }, [])

  // Voice-agent tool calls and runtime events (AVR-550). `agent_activity` is a
  // nullable top-level key: null for calls that made none, for organizations on
  // the redacted data path, and for calls recorded before the feature shipped —
  // all normal, so it normalizes to [] and the Events tab renders as it does today.
  // Both kinds are section-tagged onto one timeline array, the way `analysis`
  // already carries mixed sections. Ids are index-based rather than derived from
  // the payload: the producer emits each tool call twice and upstream collapses
  // those pairs conservatively, so an occasional un-collapsed no-result entry
  // arrives and must render as its own row. De-duplicating here would reintroduce
  // exactly the failure mode upstream avoids — two genuine invocations of the
  // same tool, same arguments, one turn is a real case.
  const { tool_calls: toolCalls = [], events: agentEvents = [] } = call.agent_activity || {}
  call.agentActivityEvents = [
    ...toolCalls.map((toolCall, index) => ({
      ...toolCall,
      id: `tool-call-${index}`,
      // transcript_turn_start_time, NOT turn_start_time. The latter is the producer's
      // raw nine-decimal value and matches no speech block: assemble_postcall rewrites
      // a block as round(time + offset, 3) before serialising it. The BE projects the
      // former into the transcript's own frame so it compares exactly equal to a
      // block's timestamp, which is what makes the inline "event triggered" marker in
      // ConversationBlock (a strict ===) fire. Measured on a staging call before the
      // projection existed: 0 of 4 tool calls attributable; after, 4 of 4.
      // It is nullable, so fall back to the raw value for placement — the row still
      // sorts and renders at roughly the right point, it just won't mark a turn.
      timestamp: toolCall.transcript_turn_start_time ?? toolCall.turn_start_time,
      section: TOOL_CALL_SECTION,
    })),
    ...agentEvents.map((event, index) => ({
      ...event,
      id: `agent-event-${index}`,
      timestamp: event.time,
      section: AGENT_EVENT_SECTION,
    })),
  ]

  // Ensure pretty_transcript defaults to an empty array if not present
  call.pretty_transcript = call.pretty_transcript || []

  return call
}

export const fetchCallExplorer =
  ({ id, keywords }) =>
  // eslint-disable-next-line consistent-return
  async (dispatch) => {
    dispatch(setCallExplorerLoading(true))
    try {
      const querystring = new URLSearchParams()
      keywords?.map((keyword) => querystring.append('keywords', keyword))
      const queryStr = querystring.toString()

      const call = await dispatch(fetchCall(id, queryStr))
      dispatch(setCallExplorerCall({ ...call, callId: id }))

      await dispatch(fetchMediaForCallId(id, true, call.metadata.screen_capture_available, false))
    } catch (err) {
      if (err?.message === `Call hasn't ended yet, can't report`) {
        return dispatch(setError({ callExplorer: true }))
      }
      if (err?.redirect_to) {
        return history.push(`${err.redirect_to}`)
      }

      return toast.error('Failed to fetch call', { cause: err })
    } finally {
      dispatch(setCallExplorerLoading(false))
    }
  }

export const fetchAgentsByOrg = (organizationId) => async (dispatch) => {
  dispatch(setLoading({ agents: true }))

  try {
    const { users } = await fetchingAPI(
      `${apiService.core}/api/organizations/${organizationId}/users`,
      'GET',
      dispatch
    )

    dispatch(setData({ agents: getAgentOptions(users) }))
  } catch (err) {
    toast.error('Failed to fetch agents', { cause: err })
  } finally {
    dispatch(setLoading({ agents: false }))
  }
}

export const fetchTagsByOrg = (organizationId) => async (dispatch) => {
  dispatch(setLoading({ tags: true }))

  try {
    const [tags, tagCategories] = await Promise.all([
      fetchingAPI(`${apiService.core}/api/organizations/${organizationId}/tags`, 'GET', dispatch),
      fetchingAPI(
        `${apiService.core}/api/organizations/${organizationId}/tags/categories`,
        'GET',
        dispatch
      ),
    ])

    const tagsOptionsWithCategories = getTagsWithCategories(tags, tagCategories)

    dispatch(setData({ tags: tagsOptionsWithCategories }))
  } catch (err) {
    toast.error('Failed to fetch tags', { cause: err })
  } finally {
    dispatch(setLoading({ tags: false }))
  }
}

export const fetchPlaybooksByOrg = (organizationId) => async (dispatch) => {
  dispatch(setLoading({ playbooks: true }))

  try {
    const configProperties = queryString.stringify({ requested_properties: 'id,name,cid' })
    const playbooks = await fetchingAPI(
      `${apiService.core}/api/${organizationId}/configs?${configProperties}`,
      'GET',
      dispatch
    )
    const playbookOptions = playbooks
      .map((playbook) => ({ value: playbook.cid, label: playbook.name }))
      .sort((a, b) => a.label.localeCompare(b.label))

    dispatch(setData({ playbooks: playbookOptions }))
  } catch (err) {
    toast.error('Failed to fetch playbooks', { cause: err })
  } finally {
    dispatch(setLoading({ playbooks: false }))
  }
}

export const fetchVoipCampaignsAPI = async (organizationId, startDate, endDate, dispatch) => {
  const queryParams = new URLSearchParams({
    organization_id: organizationId,
  })

  if (startDate) {
    queryParams.append('start_date', moment(startDate).startOf('day').toISOString())
  }
  if (endDate) {
    queryParams.append('end_date', moment(endDate).endOf('day').toISOString())
  }

  const voipCampaigns = await fetchingAPI(
    `${apiService.reporting}/api/voip_campaigns?${queryParams.toString()}`,
    'GET',
    dispatch
  )

  return voipCampaigns
    .map(({ name }) => ({ value: name, label: name }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

export const fetchVoipCampaignsByOrg = (organizationId) => async (dispatch, getState) => {
  dispatch(setLoading({ voipCampaigns: true }))

  try {
    const { startDate, endDate } = getState().callSearch.filters

    const voipCampaignOptions = await fetchVoipCampaignsAPI(
      organizationId,
      startDate,
      endDate,
      dispatch
    )

    const currentFilters = getState().callSearch.filters.voipCampaigns
    const newVoipCampaignFilters = currentFilters.filter((f) => {
      return voipCampaignOptions.some((option) => option.value === f.value)
    })

    dispatch(setFilters({ voipCampaigns: newVoipCampaignFilters }))
    dispatch(setData({ voipCampaigns: voipCampaignOptions, voipCampaignsTimedOut: false }))
  } catch {
    dispatch(setData({ voipCampaignsTimedOut: true }))
  } finally {
    dispatch(setLoading({ voipCampaigns: false }))
  }
}

export const fetchOutboundCampaignsByOrg = (organizationId) => async (dispatch, getState) => {
  dispatch(setLoading({ outboundCampaigns: true }))

  try {
    const campaigns = await fetchingAPI(
      `${apiService.reporting}/api/ai-outbound/campaigns?organization_id=${organizationId}`,
      'GET',
      dispatch
    )
    const options = campaigns
      .map(({ id, name }) => ({ value: id, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label))

    // Drop any selected campaign that no longer exists for this org
    const currentSelected = getState().callSearch.filters.outboundCampaigns
    const prunedSelected = currentSelected.filter((selected) =>
      options.some((option) => option.value === selected.value)
    )

    dispatch(setFilters({ outboundCampaigns: prunedSelected }))
    dispatch(setData({ outboundCampaigns: options }))
  } catch {
    toast.error('Failed to fetch outbound campaigns')
  } finally {
    dispatch(setLoading({ outboundCampaigns: false }))
  }
}

export const fetchAiAgentOutcomes = () => async (dispatch) => {
  dispatch(setLoading({ aiAgentOutcomes: true }))

  try {
    const outcomes = await fetchingAPI(
      `${apiService.reporting}/api/voice-agents/outcomes`,
      'GET',
      dispatch
    )
    const options = outcomes.map((outcome) => ({
      value: outcome,
      label: startCase(outcome),
    }))

    dispatch(setData({ aiAgentOutcomes: options }))
  } catch {
    toast.error('Failed to fetch outcomes')
  } finally {
    dispatch(setLoading({ aiAgentOutcomes: false }))
  }
}

export const fetchAiAgentDisconnectionReasons = () => async (dispatch) => {
  dispatch(setLoading({ aiAgentDisconnectionReasons: true }))

  try {
    const reasons = await fetchingAPI(
      `${apiService.reporting}/api/voice-agents/disconnection-reasons`,
      'GET',
      dispatch
    )
    const options = reasons.map((reason) => ({
      value: reason,
      label: startCase(reason),
    }))

    dispatch(setData({ aiAgentDisconnectionReasons: options }))
  } catch {
    toast.error('Failed to fetch disconnection reasons')
  } finally {
    dispatch(setLoading({ aiAgentDisconnectionReasons: false }))
  }
}

export const fetchDispositionsByOrg = (organizationId) => async (dispatch) => {
  dispatch(setLoading({ dispositions: true }))

  try {
    const dispositions = await fetchingAPI(
      `${apiService.reporting}/api/dispositions?organization_id=${organizationId}`,
      'GET',
      dispatch
    )
    const dispoOptions = dispositions
      .map(({ name }) => ({ value: name, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label))
    dispatch(setData({ dispositions: dispoOptions }))
  } catch (err) {
    toast.error('Failed to fetch dispositions', { cause: err })
  } finally {
    dispatch(setLoading({ dispositions: false }))
  }
}

// Pulls the org's filterable pick_list bit fields (those with selectable
// options) so the explorer can render a dynamic filter per field. Each field
// carries its options (category_uuid + name) used both as the select options
// and the values sent to the reporting API.
export const fetchFilterableBitFields = (organizationId) => async (dispatch, getState) => {
  dispatch(setLoading({ bitFields: true }))

  // The fields endpoint can be slow for some orgs, so a request fired for an
  // earlier org can resolve after the user has already switched. Drop any
  // response whose org no longer matches the selected one, so a slow (often
  // empty) result can't clobber the fields we already loaded for the new org.
  const isStale = () => getState().callSearch.filters.organizationId !== organizationId

  try {
    const bitFields = await fetchingAPI(
      `${apiService.summary}/bit-flows/fields?${queryString.stringify({
        requested_organization_id: organizationId,
        filterable_only: true,
      })}`,
      'GET',
      dispatch
    )
    if (isStale()) {
      return
    }
    dispatch(setData({ bitFields }))
  } catch (err) {
    if (isStale()) {
      return
    }
    toast.error('Failed to fetch filterable fields', { cause: err })
    dispatch(setData({ bitFields: [] }))
  } finally {
    dispatch(setLoading({ bitFields: false }))
  }
}

export const fetchPlaybookData = (filters) => async (dispatch) => {
  dispatch(setLoading({ playbookData: true }))
  const { startDate: start_date, endDate: end_date, organizationId: organization_id } = filters

  try {
    const playbookRes = await Promise.all(
      filters.playbooks.map(async (playbook) => {
        try {
          const body = { start_date, end_date, organization_id, config_cid: playbook.value }
          return await fetchingAPI(
            `${apiService.reporting}/api/playbook_event_filters?config_cid=${playbook.value}`,
            'POST',
            dispatch,
            JSON.stringify(body)
          )
        } catch {
          return { config_cid: playbook.value, error: true }
        }
      })
    )
    const playbookResUpdated = playbookRes.filter((playbookRes) => {
      return isNil(playbookRes.error)
    })
    const updatedPlaybookData = []

    playbookResUpdated.forEach((playbook) => {
      playbook.deck = playbook?.deck?.map((category) => {
        const updatedSubcategories = category.items.map((item) => {
          if (!item.has_sub_items) {
            return item
          }
          const updatedItems = [...item.items, 'No Response Used']

          return { ...item, items: updatedItems }
        })

        return {
          ...category,
          items: updatedSubcategories,
          playbookName: playbook.playbook_name,
          cid: playbook.cid,
        }
      })
      updatedPlaybookData.push(playbook)
    })
    dispatch(
      setData({
        playbookData: updatedPlaybookData,
      })
    )
  } catch {
    // Do nothing!
  } finally {
    dispatch(setLoading({ playbookData: false }))
  }
}

export const loadInitialData = (searchParams) => async (dispatch, getState) => {
  dispatch(setCallExplorerLoading(true))

  const {
    organizationid: currentUserOrg,
    hierarchy_manager,
    edit_qa_copilot,
  } = getState().currentUser
  const { active_channels: activeChannels = [] } = getState().currentOrganization

  const isBaltoAdmin = currentUserOrg === 1
  const isHierarchyManagerWithCopilot = !isBaltoAdmin && hierarchy_manager && edit_qa_copilot

  try {
    const initialFilters = parseParams(searchParams)
    const { organizationId } = initialFilters
    const channel = initialFilters.channel || CHANNEL_TYPE.CALL
    const shouldLoadInitialData = !isEmpty(initialFilters)
    const shouldBlockCrossOrgRequestForFilterData =
      isHierarchyManagerWithCopilot && organizationId !== '' && organizationId !== currentUserOrg

    if (!shouldLoadInitialData) {
      return
    }

    // Don't fetch if org doesn't have access to the selected channel
    if (!hasChannelAccess(activeChannels, channel, isBaltoAdmin)) {
      dispatch(setCalls([]))
      return
    }

    // Ignore everything else for call_id search
    if (initialFilters.call_ids) {
      // fetch scorecards if provided and set UI filters so score columns are shown and manageable in call explorer
      if (!isEmpty(initialFilters.scorecards)) {
        await dispatch(fetchScorecardsByOrg(organizationId))
      }

      await dispatch(applyFiltersAndFetchCalls(initialFilters))
    } else {
      if (!shouldBlockCrossOrgRequestForFilterData) {
        await Promise.all([
          dispatch(fetchAgentsByOrg(organizationId)),
          dispatch(fetchTagsByOrg(organizationId)),
          dispatch(fetchPlaybooksByOrg(organizationId)),
          dispatch(fetchScorecardsByOrg(organizationId)),
        ])
      }
      // after we get all the api responses map playbook/tags/agent ids
      // to their associated dropdown
      const formattedFilters = dispatch(mapParamIds(initialFilters))
      const { playbooks: formattedPlaybooks } = formattedFilters
      if (formattedPlaybooks?.length === 1) {
        await dispatch(fetchPlaybookData(formattedFilters))
      }

      dispatch(setFilters(formattedFilters))
      await dispatch(fetchCalls({ filters: formattedFilters }))
    }
  } catch (error) {
    console.error(error)
    toast.error('Failed to load initial query', { cause: error })
  } finally {
    dispatch(setCallExplorerLoading(false))
  }
}

export const loadDefaultCalls = () => async (dispatch, getState) => {
  dispatch(setCallExplorerLoading(true))

  const {
    organizationid: currentUserOrgId,
    own_organization_id: ownOrganizationId,
    hierarchy_manager: isHierarchyManager,
  } = getState().currentUser
  const { filters } = getState().callSearch
  const { active_channels: activeChannels = [] } = getState().currentOrganization
  const channel = filters.channel || CHANNEL_TYPE.CALL
  const isBaltoAdmin = currentUserOrgId === 1
  const childOrgSelected = isHierarchyManager && currentUserOrgId !== ownOrganizationId

  // Don't fetch if org doesn't have access to the selected channel
  if (!hasChannelAccess(activeChannels, channel, isBaltoAdmin)) {
    dispatch(setCalls([]))
    dispatch(setCallExplorerLoading(false))
    return
  }

  try {
    // Check if a default filter exists, unless using hierarchy child org
    if (!childOrgSelected) {
      await dispatch(fetchSavedFilters(CALL_EXPLORER_FILTER_TYPE))
      const callExplorerSavedFilters =
        getState().savedFilters.savedFilterList[CALL_EXPLORER_FILTER_TYPE]

      // Find the default saved filter, if exists
      const defaultFilter = callExplorerSavedFilters.find((filter) => filter.is_default)

      if (defaultFilter && !isEmpty(defaultFilter.filters)) {
        dispatch(setSavedFilterById(defaultFilter.uuid))
      } else {
        // Fetch the default
        dispatch(fetchCalls({ filters: { ...filters, organizationId: currentUserOrgId } }))
      }
    } else {
      // Fetch the default
      dispatch(fetchCalls({ filters: { ...filters, organizationId: currentUserOrgId } }))
    }
  } catch (error) {
    toast.error('Failed to load initial query', { cause: error })
  } finally {
    dispatch(setCallExplorerLoading(false))
  }
}

export const fetchQAScoresByCallId =
  (callId, organizationId, callType = CHANNEL_TYPE.CALL) =>
  async (dispatch) => {
    if (!callId || !organizationId) return
    dispatch(setCallExplorerData({ qaScores: [], associatedCallIds: [] }))
    dispatch(setLoading({ qaScores: true }))

    try {
      const response = await fetchingAPI(
        `${apiService.scorecard}/scoring/scores/by_call_id/${callId}?requested_organization_id=${organizationId}&call_type=${callType}`,
        'GET',
        dispatch
      )

      const associatedCallIds = [
        ...new Set(response.flatMap((scorecard) => scorecard.associated_calls)),
      ]

      dispatch(setCallExplorerData({ qaScores: response, associatedCallIds }))
    } catch (err) {
      toast.error('Failed to fetch QA scores', { cause: err })
    } finally {
      dispatch(setLoading({ qaScores: false }))
    }
  }

export const fetchCallSummaryByCallId = (callId) => async (dispatch) => {
  dispatch(setLoading({ callSummary: true }))

  try {
    const response = await fetchingAPI(`${apiService.summary}/summaries/${callId}`, 'GET', dispatch)

    dispatch(setCallExplorerData({ callSummary: response }))
  } catch (err) {
    if (err?.status !== 404) {
      toast.error(err?.message || 'Failed to fetch call summary')
    }
  } finally {
    dispatch(setLoading({ callSummary: false }))
  }
}

export const updateScoreCriteria =
  (scorecardId, updatedCriteria, organizationId) => async (dispatch, getState) => {
    dispatch(setLoading({ qaScores: true }))

    const params = queryString.stringify(
      { requested_organization_id: organizationId },
      { skipNull: true }
    )
    const url = `${apiService.scorecard}/scoring/scores/${scorecardId}/update_criteria?${params}`

    try {
      const response = await fetchingAPI(url, 'POST', dispatch, JSON.stringify(updatedCriteria))

      const { qaScores } = getState().callSearch.callExplorer
      const updatedScores = qaScores.map((score) => (score.id === scorecardId ? response : score))

      dispatch(setCallExplorerData({ qaScores: updatedScores }))
    } catch {
      toast.error('Failed to fetch playbook data')
    } finally {
      dispatch(setLoading({ qaScores: false }))
    }
  }

// Shortcut handling
export const handleShortcutNext = (filteredCriteriaOrdered) => async (dispatch, getState) => {
  const { selectedCriteriaFocusIndex } = getState().callSearch.callExplorer

  const finalCriteriaIndex = filteredCriteriaOrdered.length - 1
  if (selectedCriteriaFocusIndex < finalCriteriaIndex) {
    const nextCriteriaIndex = selectedCriteriaFocusIndex + 1
    dispatch(setCallExplorerData({ selectedCriteriaFocusIndex: nextCriteriaIndex }))
    return filteredCriteriaOrdered[nextCriteriaIndex]
  }
  return null
}

export const handleShortcutPrevious = (filteredCriteriaOrdered) => async (dispatch, getState) => {
  const { selectedCriteriaFocusIndex } = getState().callSearch.callExplorer

  if (selectedCriteriaFocusIndex > 0) {
    const previousCriteriaIndex = selectedCriteriaFocusIndex - 1
    dispatch(setCallExplorerData({ selectedCriteriaFocusIndex: previousCriteriaIndex }))
    return filteredCriteriaOrdered[previousCriteriaIndex]
  }
  return null
}

export const handleShortcutChangeScore =
  (filter, handleChangeScore, filteredCriteriaOrdered) => async (dispatch, getState) => {
    const { selectedCriteriaFocusIndex } = getState().callSearch.callExplorer
    const selectedCriteria = filteredCriteriaOrdered[selectedCriteriaFocusIndex]
    const scoreMap = { pass: 1, fail: 0, na: -1 }

    handleChangeScore({ [selectedCriteria.uuid]: scoreMap[filter] })
  }

export const resolveDispute = (criteriaScore, userId) => async (dispatch) => {
  try {
    const { dispute } = criteriaScore
    const body = {
      dispute_id: dispute.id,
      scorecard_criteria_scores_id: dispute.scorecard_criteria_scores_id,
      manager_comment: dispute.manager_comment || '',
      manager_id: userId,
      status: dispute.status,
      score: dispute.score,
    }
    return await fetchingAPI(
      `${apiService.scorecard}/disputes/criteria`,
      'PUT',
      dispatch,
      JSON.stringify(body)
    )
  } catch (err) {
    toast.error('Failed to resolve the dispute', { cause: err })
    return false
  }
}

const getScorecardSidToLabel = (scorecards = []) => {
  const map = {}
  scorecards.forEach((sc) => {
    map[sc.sid] = sc.label
  })
  return map
}

const getScorecardColumnLabels = (columns) =>
  columns
    .map((col) => col.accessor)
    .filter((accessor) => accessor && accessor.endsWith(' - Overall QA Score'))

const getCallScorecardLabelToScore = (call, sidToLabel) => {
  const map = {}
  if (Array.isArray(call.scorecards)) {
    call.scorecards.forEach((scorecard) => {
      let label = scorecard.scorecard_name
      if (!label && sidToLabel[scorecard.scorecard_sid]) {
        label = sidToLabel[scorecard.scorecard_sid]
      }
      if (label) {
        map[`${label} - Overall QA Score`] = scorecard.qa_score
      }
    })
  }
  return map
}
export const arrayOfObjectsToCsvString = (arr, textKey) => {
  if (!Array.isArray(arr)) return ''
  return arr
    .flatMap((obj) => (Array.isArray(obj.notes) ? obj.notes : [obj]))
    .map((item) => item[textKey] ?? '')
    .filter(Boolean)
    .join('\n')
}

const formatScorePercent = (score) =>
  typeof score === 'number' ? `${score.toFixed(2)}%` : score ?? ''

export const downloadCsv = (completeColumns, selectedCallIds) => async (dispatch, getState) => {
  try {
    dispatch(setLoading({ csvDownload: true }))
    const { calls, filters } = getState().callSearch

    // Set rather than Array.includes, which scans the id list once per loaded
    // call and runs twice in this function. Measured at the 10k Explorer
    // ceiling it is ~9ms per pass rather than the seconds the quadratic shape
    // suggests -- V8 handles a dense small-int array well -- so this is a
    // cheap tidy, not a rescue. Kept because 17ms of blocked main thread for
    // nothing is still nothing worth paying.
    const selectedCallIdSet = new Set(selectedCallIds)
    const selectedCalls = calls.filter((call) => selectedCallIdSet.has(call.call_id))

    const csvColumns = completeColumns.map(({ format, ...column }) => {
      if (column?.accessor === 'start_time' || column?.accessor === 'end_time') {
        return { ...column, format }
      }
      return column
    })

    const callsWithoutSummaries = selectedCalls
      .filter(({ call_summary }) => call_summary === undefined)
      .map(({ call_id }) => call_id)

    const { organizationid: currentUserOrgId } = getState().currentUser || {}
    const organizations = getState().organizations || []
    const targetOrg = organizations.find((org) => org.id === filters.organizationId)
    // QCER-802: only honor the support-access window when it belongs to the
    // org being exported — the slice's windowOrgId keeps an Org A window from
    // relaxing the cross-org guard on an Org B export.
    const supportWindowActive =
      currentUserOrgId === 1 && selectSupportWindowOpenForOrg(getState(), filters.organizationId)
    const isUnredactedCrossOrg =
      targetOrg?.unredacted_enabled === true &&
      currentUserOrgId !== filters.organizationId &&
      !supportWindowActive

    if (isUnredactedCrossOrg && !isEmpty(callsWithoutSummaries)) {
      toast.warning('Summary data is unavailable for export from unredacted organizations.')
    } else if (!isEmpty(callsWithoutSummaries)) {
      await dispatch(fetchSummariesForCallIds(callsWithoutSummaries))
    }
    const { calls: updatedCalls } = getState().callSearch
    const updatedSelectedCalls = updatedCalls.filter((call) => selectedCallIdSet.has(call.call_id))

    // Add scorecard columns to the CSV
    const sidToLabel = getScorecardSidToLabel(filters.scorecards)
    const scorecardColumnLabels = getScorecardColumnLabels(csvColumns)
    const callsWithScores = updatedSelectedCalls.map((call) => {
      const updatedCall = { ...call }
      updatedCall.task_notes = arrayOfObjectsToCsvString(call.task_notes, 'note_text')
      updatedCall.coaching_comments = arrayOfObjectsToCsvString(call.coaching_comments, 'note_text')
      const labelToScore = getCallScorecardLabelToScore(call, sidToLabel)
      scorecardColumnLabels.forEach((colLabel) => {
        updatedCall[colLabel] = formatScorePercent(labelToScore[colLabel])
      })
      return updatedCall
    })

    const { data: { tags: tagCategoriesData = [] } = {} } = getState().callSearch
    const callsWithTags = enrichCallsWithTagCategories(callsWithScores, tagCategoriesData).map(
      (call) => {
        const updatedCall = { ...call }
        Object.keys(updatedCall).forEach((key) => {
          if (key.startsWith('tags-') && Array.isArray(updatedCall[key])) {
            updatedCall[key] = updatedCall[key].join(', ')
          }
        })
        return updatedCall
      }
    )

    const response = {
      data: callsWithTags || [],
      headers: csvColumns,
      filters: { startDate: filters.startDate, endDate: filters.endDate },
      fileName: 'Call Explorer Export',
    }

    if (!isEmpty(response.data)) {
      const csvString = parseToCsv(response)
      exportCsv(csvString, response.filters || filters, response.fileName)
    }
  } catch (err) {
    toast.error('Failed to download CSV', { cause: err })
  } finally {
    dispatch(setLoading({ csvDownload: false }))
  }
}

const collectSummaryKeys = (calls) => {
  const seenKeys = new Set()
  const summaryKeys = []
  calls.forEach((call) => {
    const summary = call.call_summary || {}
    Object.keys(summary).forEach((key) => {
      if (!seenKeys.has(key)) {
        seenKeys.add(key)
        summaryKeys.push(key)
      }
    })
  })
  return summaryKeys
}

export const downloadCallsCsv = () => async (dispatch, getState) => {
  dispatch(setLoading({ csvDownload: true }))
  const controller = new AbortController()
  dispatch(setCsvAbortController(controller))

  const { filters, totalCount, calls, data } = getState().callSearch
  const exceedsLimit = totalCount > CALL_SEARCH_RESULTS_LIMIT
  const toastId = exceedsLimit
    ? toast.loading('Preparing your download. This may take several minutes for large exports.')
    : toast.loading('Preparing your download...')

  try {
    const { username, organizationid, token } = getState().currentUser
    const { limit, ...body } = formatCallFilters(filters, data.playbookData)
    body.allow_unlimited_date_range = true

    if (!exceedsLimit) {
      body.limit = limit
    }

    // Send summary keys so the backend can emit CSV headers immediately
    // without consuming the first batch to discover column names.
    if (!isEmpty(calls)) {
      const summaryKeys = collectSummaryKeys(calls)
      if (!isEmpty(summaryKeys)) {
        body.summary_keys = summaryKeys
      }
    }

    const response = await fetch(`${apiService.reporting}/api/calls/csv`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'auth-type': 'web',
        username,
        organizationid,
        token,
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    })
    if (!response.ok) {
      let errorMessage = 'Failed to download CSV'
      try {
        const errorData = await response.json()
        if (errorData.detail) {
          errorMessage = errorData.detail
        }
      } catch {
        // use the default message
      }
      toast.error(errorMessage, { id: toastId })
      return
    }

    const reader = response.body.getReader()
    const cancelOnAbort = () => reader.cancel()
    controller.signal.addEventListener('abort', cancelOnAbort)
    const chunks = []
    let bytesReceived = 0
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read()
      if (done) {
        break
      }
      chunks.push(value)
      bytesReceived += value.length
      toast.loading(`Downloading... ${(bytesReceived / (1024 * 1024)).toFixed(1)} MB received`, {
        id: toastId,
      })
    }
    controller.signal.removeEventListener('abort', cancelOnAbort)
    if (controller.signal.aborted) {
      const abortError = new Error('The operation was aborted.')
      abortError.name = 'AbortError'
      throw abortError
    }
    const blob = new Blob(chunks, { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `Balto Call Explorer Export ${moment(filters.startDate).format('YYYY-MM-DD')} - ${moment(filters.endDate).format('YYYY-MM-DD')}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    toast.success('Download complete.', { id: toastId })
  } catch (error) {
    if (error.name === 'AbortError') {
      toast.dismiss(toastId)
    } else {
      toast.error('Failed to download CSV', { id: toastId, cause: error })
    }
  } finally {
    dispatch(setCsvAbortController(null))
    dispatch(setLoading({ csvDownload: false }))
  }
}

export const fetchPlaybookForCallExplorer = (playbookId) => {
  return async (dispatch) => {
    dispatch(setLoading({ playbook: true }))

    try {
      const { config } = await fetchingAPI(
        `${apiService.core}/api/configs/${playbookId}`,
        'GET',
        dispatch
      )

      dispatch(
        setCallExplorerData({
          playbook: { ...config, body: defaultsDeep(config.body, initialPlaybookBody) },
        })
      )
    } catch (err) {
      console.warn('Failed to fetch playbook for call explorer', err)
    } finally {
      dispatch(setLoading({ playbook: false }))
    }
  }
}

// Page names recorded here and read back as the "Call Views" columns on the Supervisor Activity
// and QA Analyst Activity reports
export const CALL_DETAIL_PAGE = 'call_detail'
export const COACHING_CALL_DETAIL_PAGE = 'coaching_call_detail'

export const trackPageView = (page, callId) => (dispatch) => {
  fetchingAPI(
    `${apiService.reporting}/page-views`,
    'POST',
    dispatch,
    JSON.stringify({ page, call_id: callId })
  )
}
